import { useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Input } from "@vimos/ui";
import type { MediaItem } from "@vimos/api-client";
import { useAuth } from "../../lib/auth";
import { fetchMediaItems, mediaPublicUrl, uploadMediaFile } from "./mediaUtils";

interface MediaPickerProps {
  value: string | string[];
  label: string;
  onChange: (value: string | string[]) => void;
  multiple?: boolean;
  maxItems?: number;
}

export function MediaPicker({ value, label, onChange, multiple = false, maxItems = 10 }: MediaPickerProps) {
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [altText, setAltText] = useState("");
  const [uploadError, setUploadError] = useState<string | null>(null);

  const selectedUrls = useMemo(() => (Array.isArray(value) ? value : value ? [value] : []), [value]);

  const {
    data: media,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["media"],
    queryFn: fetchMediaItems,
    enabled: isOpen,
  });

  const filteredMedia = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return (media ?? []).filter((item) => {
      const haystack = `${item.file_name ?? ""} ${item.storage_path} ${item.alt_text ?? ""}`.toLowerCase();
      return !normalizedQuery || haystack.includes(normalizedQuery);
    });
  }, [media, query]);

  const uploadMutation = useMutation({
    mutationFn: async () => {
      setUploadError(null);
      const file = fileInputRef.current?.files?.[0];
      if (!file) throw new Error("Selecciona una imagen.");
      return uploadMediaFile(file, altText, profile?.id);
    },
    onSuccess: (item) => {
      queryClient.invalidateQueries({ queryKey: ["media"] });
      const url = mediaPublicUrl(item);
      if (multiple) {
        const next = [...selectedUrls, url];
        onChange(next.length > maxItems ? next.slice(0, maxItems) : next);
      } else {
        onChange(url);
      }
      setIsOpen(false);
      setAltText("");
      if (fileInputRef.current) fileInputRef.current.value = "";
    },
    onError: (issue) => {
      setUploadError(issue instanceof Error ? issue.message : "No pudimos subir la imagen.");
    },
  });

  function choose(item: MediaItem) {
    const url = mediaPublicUrl(item);
    if (multiple) {
      const exists = selectedUrls.includes(url);
      const next = exists ? selectedUrls.filter((u) => u !== url) : [...selectedUrls, url];
      onChange(next.length > maxItems ? next.slice(0, maxItems) : next);
    } else {
      onChange(url);
      setIsOpen(false);
    }
  }

  function removeSelected(url: string) {
    if (multiple) {
      onChange(selectedUrls.filter((u) => u !== url));
    } else {
      onChange("");
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <label className="font-body text-ink text-sm font-medium">{label}</label>
      <div className="border-sand/70 bg-paper overflow-hidden rounded-md border">
        {selectedUrls.length > 0 ? (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {selectedUrls.map((url) => (
              <div key={url} className="relative aspect-video">
                <img src={url} alt="" className="h-full w-full rounded-md object-cover" loading="lazy" />
                <button
                  type="button"
                  onClick={() => removeSelected(url)}
                  className="bg-ink/70 hover:bg-ink absolute right-1 top-1 rounded-full px-2 py-0.5 text-xs text-white"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-mist flex aspect-[16/7] items-center justify-center bg-[#f7f7f4] text-sm">
            Sin imagen seleccionada
          </div>
        )}
        <div className="flex flex-wrap gap-2 p-3">
          <Button type="button" size="sm" variant="outline" onClick={() => setIsOpen(true)}>
            {selectedUrls.length > 0 ? "Cambiar" : "Seleccionar de Media"}
          </Button>
          {selectedUrls.length > 0 && (
            <Button type="button" size="sm" variant="ghost" onClick={() => onChange(multiple ? [] : "")}>
              Quitar
            </Button>
          )}
        </div>
      </div>

      {isOpen ? (
        <div className="bg-ink/55 fixed inset-0 z-50 overflow-y-auto p-4 backdrop-blur-sm">
          <div className="bg-paper shadow-card-hover mx-auto max-w-5xl rounded-md">
            <div className="border-sand/40 flex flex-col gap-3 border-b p-5 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="font-display text-ink text-2xl font-bold">Biblioteca multimedia</h2>
                <p className="text-mist mt-1 text-sm">
                  {multiple
                    ? `Selecciona hasta ${maxItems} imágenes. Click para seleccionar/deseleccionar.`
                    : "Selecciona una imagen existente o sube una nueva."}
                </p>
              </div>
              <Button type="button" variant="ghost" onClick={() => setIsOpen(false)}>
                Cerrar
              </Button>
            </div>

            <div className="grid gap-5 p-5 lg:grid-cols-[1fr_320px]">
              <section>
                <Input label="Buscar" value={query} onChange={(event) => setQuery(event.target.value)} />
                {isLoading ? <p className="text-mist mt-5 text-sm">Cargando imagenes...</p> : null}
                {error ? <p className="mt-5 text-sm text-red-600">No pudimos cargar Media.</p> : null}
                {filteredMedia.length === 0 && !isLoading && !error ? (
                  <p className="text-mist mt-5 text-sm">No hay imagenes que coincidan.</p>
                ) : null}
                <div className="mt-5 grid max-h-[520px] gap-3 overflow-y-auto sm:grid-cols-2 xl:grid-cols-3">
                  {filteredMedia.map((item) => {
                    const url = mediaPublicUrl(item);
                    const isSelected = selectedUrls.includes(url);
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => choose(item)}
                        className={`bg-paper shadow-card hover:border-signal overflow-hidden rounded-md border text-left transition-colors ${
                          isSelected ? "border-gold ring-gold/40 ring-2" : "border-sand/50"
                        }`}
                      >
                        <div className="relative">
                          <img
                            src={url}
                            alt={item.alt_text ?? ""}
                            className="aspect-[16/10] w-full object-cover"
                            loading="lazy"
                          />
                          {isSelected && (
                            <span className="bg-gold text-navy absolute right-2 top-2 rounded-full px-2 py-0.5 text-xs font-semibold">
                              {multiple ? "Seleccionada" : "Actual"}
                            </span>
                          )}
                        </div>
                        <span className="text-ink block truncate p-3 text-sm font-medium">
                          {item.file_name ?? item.storage_path}
                        </span>
                        <span className="text-mist block truncate px-3 pb-3 text-xs">
                          {item.alt_text ?? "Sin alt text"}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </section>

              <section className="border-sand/40 rounded-md border bg-[#f7f7f4] p-4">
                <h3 className="font-display text-ink text-xl font-bold">Subir nueva imagen</h3>
                <div className="mt-4 flex flex-col gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label className="font-body text-ink text-sm font-medium">Archivo</label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
                      className="border-sand/70 bg-paper text-ink rounded-md border px-3 py-2 text-sm"
                    />
                    <p className="text-mist text-xs">Maximo 5 MB. JPG, PNG, WebP, GIF o SVG.</p>
                  </div>
                  <Input
                    label="Alt text"
                    value={altText}
                    onChange={(event) => setAltText(event.target.value)}
                  />
                  {uploadError ? <p className="text-sm text-red-600">{uploadError}</p> : null}
                  <Button
                    type="button"
                    isLoading={uploadMutation.isPending}
                    onClick={() => uploadMutation.mutate()}
                  >
                    Subir y usar
                  </Button>
                </div>
              </section>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
